import React, { useMemo } from 'react';
import {
  Accordion,
  Badge,
  Box,
  Button,
  Text,
  Stack,
  Group,
  ActionIcon,
  Flex,
  Modal,
} from '@mantine/core';
import DynamicLayoutItem from './DynamicLayoutItem';
import { FormValuesType, UiEntitySection, UiEntitySectionGroup } from 'src/UI/dynamicUI/types';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import styles from './DynamicEntity.module.css';
import { useDisclosure } from '@mantine/hooks';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import { UseFormReturnType } from '@mantine/form';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { DynamicUiContext, useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';

interface DynamicEntitySectionProps {
  section: UiEntitySectionGroup;
}

export default function DynamicEntitySection({ section }: DynamicEntitySectionProps) {
  const renderAccordionItems = (items: UiEntitySection[]) => {
    return items.map(item => (
      <FormAccordionItem value={item.label} key={item.label} label={item.label}>
        {item.children.map((item, i) => (
          <DynamicLayoutItem key={i} item={item} />
        ))}
      </FormAccordionItem>
    ));
  };

  return (
    <FormAccordion defaultValue={section.children.map(c => c.label)}>
      {renderAccordionItems(section.children)}
    </FormAccordion>
  );
}

type FormAccordionProps = {
  defaultValue?: string[];
  children?: React.ReactNode;
};

type FormAccordionItemProps = {
  label: string;
  value: string;
  children: React.ReactNode | React.ReactNode[];
  childrenWrapper?: React.ComponentType<{ children?: React.ReactNode }> | null;
  titleIcon?: React.ReactNode;
};

export const FormAccordionItem = ({
  label,
  value,
  children,
  childrenWrapper: ChildrenWrapper = FormAccordionItemContainer,
  titleIcon,
}: FormAccordionItemProps) => {
  const wrappedChildren = ChildrenWrapper ? (
    <ChildrenWrapper>{children}</ChildrenWrapper>
  ) : (
    <>{children}</>
  );

  return (
    <Accordion.Item role="region" key={label} value={value}>
      <Accordion.Control>
        <Group gap="xs">
          {label}
          {titleIcon}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>{wrappedChildren}</Accordion.Panel>
    </Accordion.Item>
  );
};

export const FormAccordionItemContainer = ({ children }: { children?: React.ReactNode }) => (
  <Box className={styles.accordionPanel}>
    <Stack gap="sm">{children}</Stack>
  </Box>
);

type FormAccordionItemListProps = {
  label: string;
  value: string;
  children: React.ReactNode[];
  isEditing: boolean;
  counterLabel: string;
  button: {
    label: string;
    onClick: () => void;
  };
  form: UseFormReturnType<FormValuesType>;
  entityType: string;
  entityId?: number | string;
  titleIcon?: React.ReactNode;
};

export const FormAccordionItemList = ({
  label,
  value,
  children,
  button,
  isEditing,
  counterLabel,
  form,
  entityType,
  titleIcon,
}: FormAccordionItemListProps) => {
  return (
    <Accordion.Item role="region" key={label} value={value}>
      <Accordion.Control mb={'xs'}>
        <Group gap="xs">
          {label}
          {titleIcon}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        {children.map((child, index) => (
          <Box key={index} data-testid={'accordion-list-item'} mt={index === 0 ? undefined : 'xxl'}>
            <Group justify={'space-between'}>
              <Badge
                className={styles.accordionBadge}
                radius={'sm'}
                size={'lg'}
                px={'xxs'}
                rightSection={
                  <Box bg={'gray.1'} style={{ borderRadius: 3 }} px={6}>
                    <Text fw={500} size={'xs'}>
                      {index + 1}
                    </Text>
                  </Box>
                }
              >
                <Text fw={500} size={'xs'}>
                  {counterLabel}
                </Text>
              </Badge>
              <ListDeleteButton form={form} entityType={entityType} index={index} />
            </Group>

            <Box className={styles.accordionPanel}>
              <Stack gap="sm">{child}</Stack>
            </Box>
          </Box>
        ))}
        {isEditing && (
          <Button
            mt={'xxl'}
            variant={'secondary'}
            onClick={button.onClick}
            data-testid={'add-button'}
            leftSection={<IconPlus size={16} />}
          >
            {button.label}
          </Button>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

type DeleteButtonProps = {
  form: UseFormReturnType<FormValuesType>;
  entityType: string;
  index: number;
};
export const ListDeleteButton = ({ form, entityType, index }: DeleteButtonProps) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [deletionIndex, setDeletionIndex] = React.useState<number | null>(null);

  const handleDeletePress = (index: number) => {
    setDeletionIndex(index);
    open();
  };

  return (
    <>
      {deletionIndex !== null && (
        <DynamicListEntityItemDeletionModal
          form={form}
          entityType={entityType}
          index={deletionIndex}
          opened={opened}
          close={close}
        />
      )}
      <ActionIcon
        data-testid="delete-button"
        variant={'subtle'}
        color={'red.7'}
        onClick={() => handleDeletePress(index)}
      >
        <IconTrash />
      </ActionIcon>
    </>
  );
};

export const FormAccordion = ({ defaultValue, children }: FormAccordionProps) => {
  return (
    <Accordion multiple defaultValue={defaultValue} chevronPosition={'left'} variant={'form'}>
      {children}
    </Accordion>
  );
};

type DeletionModalProps = {
  opened: boolean;
  close: () => void;
  form: UseFormReturnType<FormValuesType>;
  entityType: string;
  index: number;
};

export const DynamicListEntityItemDeletionModal = ({
  opened,
  close,
  form,
  entityType,
  index,
}: DeletionModalProps) => {
  const { clientId } = useDynamicUI();
  const { deleteEntityByTypeAndId } = useDeleteEntities();
  const handleDeleteConfirm = async () => {
    const entityArray = form.getValues()[entityType];
    if (Array.isArray(entityArray)) {
      try {
        if (clientId && entityArray[index].id) {
          await deleteEntityByTypeAndId(clientId, entityType, entityArray[index].id.toString());
        }

        // setting value instead of using form.removeListItem because it causes issues with the dirty state of the form
        const newArray = entityArray.filter((_, i) => i !== index);
        form.setValues({ [entityType]: newArray });
        form.setDirty({ [entityType]: true });
      } catch (e) {
        notifyAndLogError(
          'Failed to delete item',
          'Something went wrong deleting this item. Please try again and contact support if the issue persists',
          e,
          {
            entityType,
            entity: entityArray[index],
          },
        );
      }
    }

    close();
  };

  return (
    <Modal opened={opened} onClose={close} title="Are you sure?" centered>
      <Text c="gray.10">Once deleted you cannot recover this item.</Text>
      <Flex justify="flex-end" mt="xl">
        <Button onClick={handleDeleteConfirm} type="button" mr="xs">
          Yes
        </Button>
        <Button variant={'default'} onClick={close}>
          No
        </Button>
      </Flex>
    </Modal>
  );
};

export const ReadOnlyWrapper = ({ children }: { children: React.ReactNode }) => {
  const context = useDynamicUI();
  const readOnlyContext = useMemo(
    () => ({
      ...context,
      isEditing: false,
      parentIsEditing: context.parentIsEditing ?? context.isEditing,
    }),
    [context],
  );

  return <DynamicUiContext value={readOnlyContext}>{children}</DynamicUiContext>;
};
